import CloseIcon from "@mui/icons-material/Close";
import type { DialogProps as MuiDialogProps } from "@mui/material";
import {
  DialogActions,
  DialogContent,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
} from "@mui/material";
import type { FormikValues } from "formik";
import { useCallback, useEffect, useState } from "react";

import { IconButton } from "@citrine/atoms/icon-button";
import type { Optional } from "@citrine/utils/types";

import * as sx from "./dialog.sx";
import type { FormProps } from "./form";

interface TitleProps {
  title: React.ReactNode;
  onClose?: () => void;
}

function DialogTitle({ title, onClose }: TitleProps) {
  return (
    <MuiDialogTitle>
      {title}
      {onClose ? (
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
}

export interface DialogProps<Values extends FormikValues = FormikValues>
  extends
    Optional<FormProps<Values>, "initialValues">,
    Partial<TitleProps>,
    Pick<MuiDialogProps, "classes" | "maxWidth" | "style"> {
  timeout?: number;
  actions?: React.ReactNode;
  description?: React.ReactNode;
}

export function Dialog<Values extends FormikValues = FormikValues>({
  timeout = 500,
  title,
  children,
  classes: classesProp,
  actions,
  description,
  onClose,
  maxWidth,
  ...props
}: DialogProps<Values>) {
  const [open, setOpen] = useState<boolean>(false);
  // set open in async useEffect to ensure it mounts closed and animates in
  useEffect(() => {
    setOpen(Boolean(props.initialValues));
  }, [props.initialValues]);

  const onCloseCallback = useCallback(
    (_event: unknown, reason: "escapeKeyDown" | "backdropClick") => {
      // don't close from backdrop click
      if (reason === "escapeKeyDown") {
        onClose?.();
      }
    },
    [onClose]
  );

  return (
    <MuiDialog
      classes={classesProp}
      maxWidth={maxWidth}
      open={open}
      onClose={onCloseCallback}
      transitionDuration={timeout}
    >
      <sx.Form<Values>
        {...(props as FormProps<Values>)}
        // className={clsx(layout.column, styles.modal, styles[size])}
        validateOnMount={Boolean(
          // Without props.initialValues, validation occurs with the initial
          // initialValues as the modal is closing which is undesireable when
          // the user is fixing an object that is initially invalid
          props.validateOnMount && props.initialValues
        )}
      >
        {(formikProps) => (
          <>
            {title ? <DialogTitle title={title} onClose={onClose} /> : null}
            <DialogContent>
              {typeof children === "function"
                ? children(formikProps)
                : children}
            </DialogContent>
            {description ? (
              <sx.Description as="div" variant="body2">
                {description}
              </sx.Description>
            ) : null}
            {actions ? <DialogActions>{actions}</DialogActions> : null}
          </>
        )}
      </sx.Form>
    </MuiDialog>
  );
}
